import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import type { LibrarianLintResult } from '@/components/knowledge/types';

interface LibrarianLintDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  result: LibrarianLintResult | null;
  onRunLint: () => Promise<void>;
  loading?: boolean;
}

export default function LibrarianLintDialog({ open, onOpenChange, result, onRunLint, loading = false }: LibrarianLintDialogProps) {
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="overflow-hidden border-border/70 bg-background/95 p-0 shadow-2xl sm:max-w-2xl">
        <DialogHeader className="border-b border-border/60 px-6 py-5">
          <DialogTitle>Run Librarian lint</DialogTitle>
          <DialogDescription>
            Inspect the vault graph for orphans, stale pages, missing cross-links, and maintenance suggestions.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-5 px-6 py-5 text-sm">
          <div className="rounded-xl border border-border/70 bg-muted/20 p-4">
            <ul className="list-disc space-y-1 pl-5 text-muted-foreground">
              <li>Orphan pages with no inbound links</li>
              <li>Stale pages that need a refresh</li>
              <li>Pages missing useful cross-links</li>
              <li>A markdown lint report saved back into the vault</li>
            </ul>
          </div>

          {result && (
            <div className="rounded-xl border border-border/70 bg-card/70 p-4">
              <h4 className="font-medium">Latest lint summary</h4>
              <div className="mt-3 grid gap-3 md:grid-cols-3">
                <div>
                  <div className="text-xs uppercase tracking-wide text-muted-foreground">Orphans</div>
                  <div className="mt-1 text-2xl font-semibold">{result.orphanPages.length}</div>
                </div>
                <div>
                  <div className="text-xs uppercase tracking-wide text-muted-foreground">Stale pages</div>
                  <div className="mt-1 text-2xl font-semibold">{result.stalePages.length}</div>
                </div>
                <div>
                  <div className="text-xs uppercase tracking-wide text-muted-foreground">Missing cross-links</div>
                  <div className="mt-1 text-2xl font-semibold">{result.missingCrossLinks.length}</div>
                </div>
              </div>
              <div className="mt-4 text-muted-foreground">Report page: {result.reportPage.path}</div>
            </div>
          )}
        </div>

        <DialogFooter className="border-t border-border/60 px-6 py-4">
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Close
          </Button>
          <Button onClick={onRunLint} disabled={loading}>
            {loading ? 'Running…' : 'Run lint'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}